/**
 * AI Settings Page - Super Admin Only
 * Manage global AI API keys and per-tenant AI model settings
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getGlobalSettings,
  getAllTenantsAISettings,
  setGlobalAPIKey,
  deleteGlobalAPIKey,
  validateAPIKey,
  updateTenantAISettings,
  AIProvider,
  AIModel,
  TenantWithAISettings,
  PROVIDER_DISPLAY_NAMES,
  PROVIDER_COLORS,
  formatTokens,
  formatCost,
  getUsagePercentage,
} from '@/services/api/ai-settings';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Key,
  Settings,
  Building2,
  Loader2,
  Check,
  X,
  Eye,
  EyeOff,
  AlertCircle,
  Cpu,
  DollarSign,
  Zap,
  Edit,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const PROVIDERS: AIProvider[] = ['openai', 'anthropic', 'google', 'xai'];

export function AISettingsPage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('api-keys');

  // Fetch global settings
  const {
    data: globalSettings,
    isLoading: loadingGlobal,
    error: globalError,
  } = useQuery({
    queryKey: ['ai-settings', 'global'],
    queryFn: getGlobalSettings,
  });

  // Fetch all tenants settings
  const {
    data: tenants,
    isLoading: loadingTenants,
    error: tenantsError,
  } = useQuery({
    queryKey: ['ai-settings', 'tenants'],
    queryFn: getAllTenantsAISettings,
  });

  if (loadingGlobal || loadingTenants) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (globalError || tenantsError) {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] text-center">
        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
        <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
        <p className="text-muted-foreground">
          You need super admin privileges to access AI Settings.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
          <Cpu className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">AI Settings</h1>
          <p className="text-sm text-muted-foreground">
            Manage AI provider API keys and tenant model settings
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="api-keys" className="flex items-center gap-2">
            <Key className="h-4 w-4" />
            API Keys
          </TabsTrigger>
          <TabsTrigger value="tenants" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Tenant Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="api-keys" className="mt-6">
          <APIKeysTab
            apiKeys={globalSettings?.apiKeys || []}
            availableModels={globalSettings?.availableModels || []}
          />
        </TabsContent>

        <TabsContent value="tenants" className="mt-6">
          <TenantsTab
            tenants={tenants || []}
            availableModels={globalSettings?.availableModels || []}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// =====================
// API Keys Tab
// =====================

function APIKeysTab({
  apiKeys,
  availableModels,
}: {
  apiKeys: { provider: AIProvider; keyName?: string; isActive: boolean; isConfigured: boolean; totalTokensUsed: number; totalCost: number; lastUsedAt?: string }[];
  availableModels: AIModel[];
}) {
  const queryClient = useQueryClient();
  const [editingProvider, setEditingProvider] = useState<AIProvider | null>(null);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [apiSecretInput, setApiSecretInput] = useState('');
  const [keyNameInput, setKeyNameInput] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<{ valid: boolean; error?: string } | null>(null);

  const setKeyMutation = useMutation({
    mutationFn: (data: { provider: AIProvider; apiKey: string; apiSecret?: string; keyName?: string }) =>
      setGlobalAPIKey(data),
    onSuccess: () => {
      toast.success('API key saved successfully');
      queryClient.invalidateQueries({ queryKey: ['ai-settings'] });
      closeDialog();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to save API key');
    },
  });

  const deleteKeyMutation = useMutation({
    mutationFn: deleteGlobalAPIKey,
    onSuccess: () => {
      toast.success('API key deactivated');
      queryClient.invalidateQueries({ queryKey: ['ai-settings'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete API key');
    },
  });

  const handleValidate = async () => {
    if (!editingProvider || !apiKeyInput) return;
    setValidating(true);
    try {
      const result = await validateAPIKey(editingProvider, apiKeyInput);
      setValidationResult(result);
    } catch {
      setValidationResult({ valid: false, error: 'Validation failed' });
    }
    setValidating(false);
  };

  const handleSave = () => {
    if (!editingProvider || !apiKeyInput) return;
    setKeyMutation.mutate({
      provider: editingProvider,
      apiKey: apiKeyInput,
      apiSecret: apiSecretInput || undefined,
      keyName: keyNameInput || undefined,
    });
  };

  const closeDialog = () => {
    setEditingProvider(null);
    setApiKeyInput('');
    setApiSecretInput('');
    setKeyNameInput('');
    setShowKey(false);
    setValidationResult(null);
  };

  // Group models by provider
  const modelsByProvider = availableModels.reduce((acc, model) => {
    if (!acc[model.provider]) acc[model.provider] = [];
    acc[model.provider].push(model);
    return acc;
  }, {} as Record<AIProvider, AIModel[]>);

  return (
    <>
      <div className="grid gap-4 md:grid-cols-2">
        {PROVIDERS.map((provider) => {
          const keyInfo = apiKeys.find((k) => k.provider === provider);
          const models = modelsByProvider[provider] || [];

          return (
            <Card key={provider}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-lg">{PROVIDER_DISPLAY_NAMES[provider]}</CardTitle>
                    <Badge
                      variant={keyInfo?.isConfigured ? 'default' : 'secondary'}
                      className={keyInfo?.isConfigured ? 'bg-green-600' : ''}
                    >
                      {keyInfo?.isConfigured ? 'Configured' : 'Not Set'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditingProvider(provider);
                        setKeyNameInput(keyInfo?.keyName || '');
                      }}
                    >
                      <Edit className="h-3.5 w-3.5 mr-1" />
                      {keyInfo?.isConfigured ? 'Update' : 'Add'}
                    </Button>
                    {keyInfo?.isConfigured && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => deleteKeyMutation.mutate(provider)}
                        disabled={deleteKeyMutation.isPending}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
                {keyInfo?.keyName && (
                  <CardDescription>Key: {keyInfo.keyName}</CardDescription>
                )}
              </CardHeader>
              <CardContent>
                {keyInfo?.isConfigured ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Total Tokens Used:</span>
                        <p className="font-medium">{formatTokens(keyInfo.totalTokensUsed)}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Total Cost:</span>
                        <p className="font-medium">{formatCost(keyInfo.totalCost)}</p>
                      </div>
                    </div>
                    {keyInfo.lastUsedAt && (
                      <p className="text-xs text-muted-foreground">
                        Last used: {new Date(keyInfo.lastUsedAt).toLocaleString()}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No API key configured. Add a key to enable this provider.
                  </p>
                )}
                <div className="mt-4 pt-3 border-t">
                  <p className="text-xs text-muted-foreground mb-2">
                    Available Models ({models.length})
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {models.slice(0, 4).map((model) => (
                      <Badge key={model.modelId} variant="outline" className="text-[10px]">
                        {model.displayName}
                      </Badge>
                    ))}
                    {models.length > 4 && (
                      <Badge variant="outline" className="text-[10px]">
                        +{models.length - 4} more
                      </Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Edit API Key Dialog */}
      <Dialog open={!!editingProvider} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingProvider && `${PROVIDER_DISPLAY_NAMES[editingProvider]} API Key`}
            </DialogTitle>
            <DialogDescription>
              Enter your API key for this provider. It will be encrypted and stored securely.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Key Name (optional)</Label>
              <Input
                placeholder="e.g., Production Key"
                value={keyNameInput}
                onChange={(e) => setKeyNameInput(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>API Key *</Label>
              <div className="relative">
                <Input
                  type={showKey ? 'text' : 'password'}
                  placeholder="sk-..."
                  value={apiKeyInput}
                  onChange={(e) => {
                    setApiKeyInput(e.target.value);
                    setValidationResult(null);
                  }}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowKey(!showKey)}
                >
                  {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {editingProvider === 'google' && (
              <div className="space-y-2">
                <Label>API Secret (optional)</Label>
                <Input
                  type="password"
                  placeholder="Secret key if required"
                  value={apiSecretInput}
                  onChange={(e) => setApiSecretInput(e.target.value)}
                />
              </div>
            )}

            {validationResult && (
              <div
                className={cn(
                  'flex items-center gap-2 p-3 rounded-md text-sm',
                  validationResult.valid
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                )}
              >
                {validationResult.valid ? (
                  <>
                    <Check className="h-4 w-4" />
                    API key is valid
                  </>
                ) : (
                  <>
                    <X className="h-4 w-4" />
                    {validationResult.error || 'Invalid API key'}
                  </>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={handleValidate}
              disabled={!apiKeyInput || validating}
            >
              {validating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Validate
            </Button>
            <Button
              onClick={handleSave}
              disabled={!apiKeyInput || setKeyMutation.isPending}
            >
              {setKeyMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// =====================
// Tenants Tab
// =====================

function TenantsTab({
  tenants,
  availableModels,
}: {
  tenants: TenantWithAISettings[];
  availableModels: AIModel[];
}) {
  const queryClient = useQueryClient();
  const [editingTenant, setEditingTenant] = useState<TenantWithAISettings | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<AIProvider>('openai');
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [monthlyTokenLimit, setMonthlyTokenLimit] = useState<string>('');
  const [dailyTokenLimit, setDailyTokenLimit] = useState<string>('');
  const [monthlyBudget, setMonthlyBudget] = useState<string>('');
  const [dailyBudget, setDailyBudget] = useState<string>('');

  const updateMutation = useMutation({
    mutationFn: ({ tenantId, updates }: { tenantId: string; updates: any }) =>
      updateTenantAISettings(tenantId, updates),
    onSuccess: () => {
      toast.success('Tenant AI settings updated');
      queryClient.invalidateQueries({ queryKey: ['ai-settings'] });
      setEditingTenant(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update settings');
    },
  });

  const openEditDialog = (tenant: TenantWithAISettings) => {
    setEditingTenant(tenant);
    setSelectedProvider(tenant.settings.selectedProvider);
    setSelectedModel(tenant.settings.selectedModel);
    setMonthlyTokenLimit(tenant.settings.monthlyTokenLimit.toString());
    setDailyTokenLimit(tenant.settings.dailyTokenLimit.toString());
    setMonthlyBudget(tenant.settings.monthlyBudget.toString());
    setDailyBudget(tenant.settings.dailyBudget.toString());
  };

  const handleSave = () => {
    if (!editingTenant) return;
    updateMutation.mutate({
      tenantId: editingTenant.tenantId,
      updates: {
        selectedProvider,
        selectedModel,
        monthlyTokenLimit: parseInt(monthlyTokenLimit, 10),
        dailyTokenLimit: parseInt(dailyTokenLimit, 10),
        monthlyBudget: parseFloat(monthlyBudget),
        dailyBudget: parseFloat(dailyBudget),
      },
    });
  };

  // Filter models by selected provider
  const providerModels = availableModels.filter((m) => m.provider === selectedProvider);

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Tenant AI Configuration</CardTitle>
          <CardDescription>
            Configure AI model and usage limits for each tenant. Tenants cannot see or modify these settings.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tenant</TableHead>
                <TableHead>Provider / Model</TableHead>
                <TableHead>Usage Today</TableHead>
                <TableHead>Usage This Month</TableHead>
                <TableHead>Limits</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tenants.map((tenant) => (
                <TableRow key={tenant.tenantId}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{tenant.tenantName}</p>
                      <p className="text-xs text-muted-foreground">{tenant.tenantId.slice(0, 8)}...</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <Badge className={cn('text-xs', PROVIDER_COLORS[tenant.settings.selectedProvider])}>
                        {PROVIDER_DISPLAY_NAMES[tenant.settings.selectedProvider]}
                      </Badge>
                      <p className="text-xs text-muted-foreground">{tenant.settings.selectedModel}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm">
                        <Zap className="h-3 w-3 text-muted-foreground" />
                        {formatTokens(tenant.usage.tokensUsedToday)}
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <DollarSign className="h-3 w-3 text-muted-foreground" />
                        {formatCost(tenant.usage.costToday)}
                      </div>
                      <Progress
                        value={getUsagePercentage(tenant.usage.tokensUsedToday, tenant.usage.dailyTokenLimit)}
                        className="h-1"
                      />
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm">
                        <Zap className="h-3 w-3 text-muted-foreground" />
                        {formatTokens(tenant.usage.tokensUsedThisMonth)}
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <DollarSign className="h-3 w-3 text-muted-foreground" />
                        {formatCost(tenant.usage.costThisMonth)}
                      </div>
                      <Progress
                        value={getUsagePercentage(tenant.usage.tokensUsedThisMonth, tenant.usage.monthlyTokenLimit)}
                        className="h-1"
                      />
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-xs space-y-0.5">
                      <p>Daily: {formatTokens(tenant.settings.dailyTokenLimit)} / {formatCost(tenant.settings.dailyBudget)}</p>
                      <p>Monthly: {formatTokens(tenant.settings.monthlyTokenLimit)} / {formatCost(tenant.settings.monthlyBudget)}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="outline" size="sm" onClick={() => openEditDialog(tenant)}>
                      <Settings className="h-3.5 w-3.5 mr-1" />
                      Configure
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {tenants.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    No tenants found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Tenant Dialog */}
      <Dialog open={!!editingTenant} onOpenChange={(open) => !open && setEditingTenant(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Configure AI Settings</DialogTitle>
            <DialogDescription>
              {editingTenant?.tenantName}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>AI Provider</Label>
                <Select value={selectedProvider} onValueChange={(v) => {
                  setSelectedProvider(v as AIProvider);
                  // Reset model when provider changes
                  const models = availableModels.filter((m) => m.provider === v);
                  if (models.length > 0) setSelectedModel(models[0].modelId);
                }}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROVIDERS.map((p) => (
                      <SelectItem key={p} value={p}>
                        {PROVIDER_DISPLAY_NAMES[p]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Model</Label>
                <Select value={selectedModel} onValueChange={setSelectedModel}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {providerModels.map((model) => (
                      <SelectItem key={model.modelId} value={model.modelId}>
                        {model.displayName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Daily Token Limit</Label>
                <Input
                  type="number"
                  value={dailyTokenLimit}
                  onChange={(e) => setDailyTokenLimit(e.target.value)}
                  placeholder="-1 for unlimited"
                />
              </div>
              <div className="space-y-2">
                <Label>Daily Budget ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={dailyBudget}
                  onChange={(e) => setDailyBudget(e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Monthly Token Limit</Label>
                <Input
                  type="number"
                  value={monthlyTokenLimit}
                  onChange={(e) => setMonthlyTokenLimit(e.target.value)}
                  placeholder="-1 for unlimited"
                />
              </div>
              <div className="space-y-2">
                <Label>Monthly Budget ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={monthlyBudget}
                  onChange={(e) => setMonthlyBudget(e.target.value)}
                />
              </div>
            </div>

            {editingTenant && (
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-sm font-medium mb-2">Current Usage</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Today</p>
                    <p>{formatTokens(editingTenant.usage.tokensUsedToday)} tokens / {formatCost(editingTenant.usage.costToday)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">This Month</p>
                    <p>{formatTokens(editingTenant.usage.tokensUsedThisMonth)} tokens / {formatCost(editingTenant.usage.costThisMonth)}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingTenant(null)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default AISettingsPage;
